// @flow
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { type YMap, Doc } from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { useMissionCode } from '../../../../../context'
import {
	useCanControlMission,
	useCanvasWebsocketUrl,
} from '../../../../../context/provider'
import { useJuniorPlusStateSelector } from '../../../../../context/customHooks'
import { usePrevious } from '../../../../../utility'
import { shapes } from './customShapes'
import { createTLStore, type TLStore } from '@tldraw/tldraw'

export type DocumentData = {
	id: string,
	doc: Doc,

	shapes: YMap<string, { id: string }>,
}

// Items of which we only need one per document.
type OnePerDocumentItems = {
	webSocketProvider: WebsocketProvider,
	documentData: DocumentData,
	store: TLStore,
}

type OnePerDocumentItemsMap = {
	[webSocketId: string]: OnePerDocumentItems,
}

type Context = {
	getDoc: (documentId: string) => DocumentData,
	onePerDocumentItemsRef: { current: OnePerDocumentItemsMap },
}

const CanvasConnectionContext = React.createContext<Context | void>()

const exportingSuffix = '-exporting'

/**
 * Handles websocket connection for showing multiple canvases in the player at the same time.
 */
export function CanvasConnectionProvider({
	children,
}: {
	children: React$Node,
}): React$Node {
	const _docs = React.useRef<{ [documentId: string]: DocumentData }>({})
	const onePerDocumentItemsRef = React.useRef<OnePerDocumentItemsMap>({})

	/**
	 * Gets doc data for a specific document id. Will always get
	 * the same doc data object for a given document id.
	 * @param documentId the id of the document to get a YDoc for
	 */
	const getDoc = React.useCallback((documentId: string) => {
		const key = documentId
		if (_docs.current[key]) {
			return _docs.current[key]
		}
		const doc = new Doc()
		const shapes = doc.getMap('shapes')
		_docs.current[key] = {
			id: documentId,
			doc,
			shapes,
		}
		return _docs.current[key]
	}, [])

	useCleanUpWebsocketsOnCreativeCanvasOver(onePerDocumentItemsRef)

	// Clean up all web sockets on unmount and/or page unload
	React.useEffect(() => {
		function handleDisconnect() {
			Object.keys(onePerDocumentItemsRef.current).forEach(websocketId => {
				onePerDocumentItemsRef.current[websocketId].webSocketProvider.destroy()
			})
			onePerDocumentItemsRef.current = {}
		}
		window.addEventListener('beforeunload', handleDisconnect)
		return () => {
			handleDisconnect()
			window.removeEventListener('beforeunload', handleDisconnect)
		}
	}, [])

	return (
		<CanvasConnectionContext.Provider
			value={{ getDoc, onePerDocumentItemsRef }}>
			{children}
		</CanvasConnectionContext.Provider>
	)
}

/**
 * Gets all creative canvas items which we only need one of per document. This includes the yjs websocket, the yjs document, and the tldraw store.
 * This hook will create a new websocket and store if the export status is provided.
 * @param documentId the id of the document to get items for
 * @param exportStatus If provided, signifies that this usage of `useOnePerDocumentItems` is for a Creative Canvas that is exporting the
 * resulting image. The value of this arg is the status of the export. Will be null if the websocket is not being used to export the canvas.
 * A unique set of OnePerDocumentItems will be created if this arg is provided.
 */
export function useOnePerDocumentItems(
	documentId: string,
	exportStatus: string | void
): {
	...OnePerDocumentItems,
	error: Error | null,
} {
	const context = useContext(CanvasConnectionContext)
	if (!context) {
		throw new Error(
			'Cannot access yjs websocket without CanvasConnectionContext'
		)
	}
	const { onePerDocumentItemsRef, getDoc } = context
	const websocketId = `${documentId}${exportStatus ? exportingSuffix : ''}`
	const missionCode = useMissionCode() ?? ''
	const canControl = useCanControlMission() ?? false
	const canvasWebsocketUrl = useCanvasWebsocketUrl()

	// Destroys the websocket when the export is complete.
	// Only deals with websockets that are used for exporting creative canvas documents.
	React.useEffect(() => {
		if (
			exportStatus === 'complete' &&
			onePerDocumentItemsRef.current[websocketId]
		) {
			onePerDocumentItemsRef.current[websocketId]?.webSocketProvider.destroy()
			delete onePerDocumentItemsRef.current[websocketId]
		}
	}, [exportStatus, onePerDocumentItemsRef, websocketId])

	const [error, setError] = useState<Error | null>(null)

	// In a typical useMemo, the result would be derived from the dependencies. In this case, the result comes directly from the onePerDocumentItemsRef,
	// which means that there is a minor concern about keeping the returned value of `useOnePerDocumentItems` up to date with what is actually in onePerDocumentItemsRef.
	// Keeping these items in state would require us to keep both the `onePerDocumentItemsRef` and a `onePerDocumentItems` state variable in the CreativeCanvasDocumentProvider,
	// along with some other complications, which is why I opted to only use a ref.
	const onePerDocumentItems: OnePerDocumentItems = useMemo(() => {
		if (onePerDocumentItemsRef.current[websocketId]) {
			return onePerDocumentItemsRef.current[websocketId]
		}

		if (!canvasWebsocketUrl) {
			throw new Error('Cannot connect websocket without websocket url')
		}
		const documentData = getDoc(documentId)

		const queryParams: {|
			missionCode: string,
			studentId?: string,
			isTeacher?: string,
			forceAccess?: string,
		|} = {
			missionCode: missionCode,
		}
		if (canControl) {
			queryParams.isTeacher = 'true'
		}
		if (exportStatus) {
			queryParams.forceAccess = 'true'
		}

		const webSocketProvider = new WebsocketProvider(
			canvasWebsocketUrl,
			documentId,
			documentData.doc,
			{
				params: queryParams,
			}
		)
		// Catch on message error
		if (webSocketProvider.ws?.onmessage) {
			const originalOnMessage = webSocketProvider.ws?.onmessage
			webSocketProvider.ws.onmessage = function (event) {
				try {
					originalOnMessage(event)
				} catch (error) {
					// By setting an error here, we are able to display an error message to the user through the canvas
					// and TlDraw throws the error for sentry to catch.
					setError(error)
				}
			}
		}

		onePerDocumentItemsRef.current[websocketId] = {
			webSocketProvider,
			documentData,
			store: createTLStore({ shapes }),
		}

		return onePerDocumentItemsRef.current[websocketId]
	}, [
		documentId,
		websocketId,
		getDoc,
		onePerDocumentItemsRef,
		canvasWebsocketUrl,
		canControl,
		missionCode,
		exportStatus,
	])

	return {
		...onePerDocumentItems,
		error,
	}
}

/**
 * A hook that returns true if there was a creative canvas but now there is not.
 * @returns {boolean}
 */
function useIsCreativeCanvasOver() {
	const canvasId = useJuniorPlusStateSelector(
		state => state?.stationData.creativeCanvas?.currentCanvasId
	)
	const previousCanvasId = usePrevious(canvasId)

	if (!canvasId && previousCanvasId) {
		return true
	}
}

/**
 * Destroys websockets when the creative canvas is over. Does not destroy websockets that are being used for exporting.
 */
function useCleanUpWebsocketsOnCreativeCanvasOver(onePerDocumentItemsRef: {
	current: OnePerDocumentItemsMap,
}) {
	const shouldCleanUp = useIsCreativeCanvasOver()
	useEffect(() => {
		if (shouldCleanUp) {
			Object.keys(onePerDocumentItemsRef.current).forEach(websocketId => {
				// do not destroy web-sockets that are being used for exporting. those will be destroyed above.
				if (!websocketId.includes(exportingSuffix)) {
					onePerDocumentItemsRef.current[
						websocketId
					]?.webSocketProvider.destroy()
					delete onePerDocumentItemsRef.current[websocketId]
				}
			})
		}
	}, [shouldCleanUp, onePerDocumentItemsRef])
}
